import type * as Common from '@piccolohealth/pbs-common';
import type { RowSelectionOptions } from '@piccolohealth/ui';
import { type DateTime, P } from '@piccolohealth/util';
import { useInfiniteQuery } from '@tanstack/react-query';
import React from 'react';
import { useAppContext } from '../../hooks/useAppContext';
import type { ParticipantFilesAndDocumentsFilter } from '../../hooks/useParticipantFilesAndDocumentsFilter';
import type { ParticipantNotesFilter } from '../../hooks/useParticipantNotesFilter';
import {
  createGqlQuery,
  createPaginatedGqlQuery,
  createTimestampPaginatedGqlQuery,
  type InfiniteQueryOptions,
} from '../fetcher';
import {
  Participant,
  ParticipantAuditTrailEntries,
  ParticipantBehaviour,
  ParticipantBehaviours,
  ParticipantBehavioursMinimal,
  ParticipantBehavioursRecordings,
  ParticipantDocument,
  ParticipantDocuments,
  ParticipantDocumentsMinimal,
  ParticipantDocumentVersion,
  ParticipantDocumentVersions,
  ParticipantEmbeddableForms,
  ParticipantFiles,
  ParticipantFilesMinimal,
  ParticipantMinimal,
  ParticipantNote,
  ParticipantNotes,
  ParticipantNotesMinimal,
  ParticipantNoteVersion,
  ParticipantNoteVersions,
  ParticipantQrs,
  ParticipantRestrictivePractices,
  ParticipantTasks,
  ParticipantUsers,
  ParticipantUsersMinimal,
  ParticipantWidgetLayoutItems,
} from '../queries/participant.query';
import type * as Types from '../types';

export const getParticipantBaseKey = (variables: Types.ParticipantMinimalQueryVariables) => [
  'participant',
  variables,
];

export const useParticipantQuery = createGqlQuery<
  Types.ParticipantQueryVariables,
  Types.ParticipantQuery
>((variables) => ['participant', variables], Participant);

export const useParticipantMinimalQuery = createGqlQuery<
  Types.ParticipantMinimalQueryVariables,
  Types.ParticipantMinimalQuery
>((variables) => ['participant', variables, 'minimal'], ParticipantMinimal);

export const useParticipantUsersQuery = createGqlQuery<
  Types.ParticipantUsersQueryVariables,
  Types.ParticipantUsersQuery
>((variables) => ['participant', variables, 'users'], ParticipantUsers);

export const useParticipantUsersMinimalQuery = createGqlQuery<
  Types.ParticipantUsersMinimalQueryVariables,
  Types.ParticipantUsersMinimalQuery
>((variables) => ['participant', variables, 'users', 'minimal'], ParticipantUsersMinimal);

export const useParticipantTasksQuery = createGqlQuery<
  Types.ParticipantTasksQueryVariables,
  Types.ParticipantTasksQuery
>((variables) => ['participant', variables, 'tasks'], ParticipantTasks);

export const useParticipantEmbeddableFormsQuery = createGqlQuery<
  Types.ParticipantEmbeddableFormsQueryVariables,
  Types.ParticipantEmbeddableFormsQuery
>((variables) => ['participant', variables, 'embeddableForms'], ParticipantEmbeddableForms);

export const useParticipantQrsQuery = createGqlQuery<
  Types.ParticipantQrsQueryVariables,
  Types.ParticipantQrsQuery
>((variables) => ['participant', variables, 'qrs'], ParticipantQrs);

export const useParticipantRestrictivePracticesQuery = createGqlQuery<
  Types.ParticipantRestrictivePracticesQueryVariables,
  Types.ParticipantRestrictivePracticesQuery
>(
  (variables) => ['participant', variables, 'restrictivePractices'],
  ParticipantRestrictivePractices,
);

export const useParticipantBehavioursQuery = createGqlQuery<
  Types.ParticipantBehavioursQueryVariables,
  Types.ParticipantBehavioursQuery
>((variables) => ['participant', variables, 'behaviours'], ParticipantBehaviours);

export const useParticipantBehavioursMinimalQuery = createGqlQuery<
  Types.ParticipantBehavioursMinimalQueryVariables,
  Types.ParticipantBehavioursMinimalQuery
>((variables) => ['participant', variables, 'behaviours', 'minimal'], ParticipantBehavioursMinimal);

export const useParticipantBehavioursRecordingsQuery = createGqlQuery<
  Types.ParticipantBehavioursRecordingsQueryVariables,
  Types.ParticipantBehavioursRecordingsQuery
>(
  (variables) => ['participant', variables, 'behaviours', 'recordings'],
  ParticipantBehavioursRecordings,
);

export const useParticipantBehaviourQuery = createGqlQuery<
  Types.ParticipantBehaviourQueryVariables,
  Types.ParticipantBehaviourQuery
>((variables) => ['participant', variables, 'behaviour'], ParticipantBehaviour);

const getParticipantNotesBaseKey = (variables: Types.ParticipantNotesQueryVariables) => [
  'participant',
  variables,
  'notes',
];

export const useParticipantNotesQuery = createGqlQuery<
  Types.ParticipantNotesQueryVariables,
  Types.ParticipantNotesQuery
>(getParticipantNotesBaseKey, ParticipantNotes);

export const useParticipantNotesMinimalQuery = createGqlQuery<
  Types.ParticipantNotesMinimalQueryVariables,
  Types.ParticipantNotesMinimalQuery
>((variables) => ['participant', variables, 'notes', 'minimal'], ParticipantNotesMinimal);

export const useParticipantNoteQuery = createGqlQuery<
  Types.ParticipantNoteQueryVariables,
  Types.ParticipantNoteQuery
>((variables) => ['participant', variables, 'note'], ParticipantNote);

export const useParticipantNoteVersionsQuery = createGqlQuery<
  Types.ParticipantNoteVersionsQueryVariables,
  Types.ParticipantNoteVersionsQuery
>((variables) => ['participant', variables, 'note', 'versions'], ParticipantNoteVersions);

export const useParticipantNoteVersionQuery = createGqlQuery<
  Types.ParticipantNoteVersionQueryVariables,
  Types.ParticipantNoteVersionQuery
>((variables) => ['participant', variables, 'note', 'version'], ParticipantNoteVersion);

export const useParticipantFilesQuery = createGqlQuery<
  Types.ParticipantFilesQueryVariables,
  Types.ParticipantFilesQuery
>((variables) => ['participant', variables, 'files'], ParticipantFiles);

export const useParticipantFilesMinimalQuery = createGqlQuery<
  Types.ParticipantFilesMinimalQueryVariables,
  Types.ParticipantFilesMinimalQuery
>((variables) => ['participant', variables, 'files', 'minimal'], ParticipantFilesMinimal);

export const useParticipantDocumentsQuery = createGqlQuery<
  Types.ParticipantDocumentsQueryVariables,
  Types.ParticipantDocumentsQuery
>((variables) => ['participant', variables, 'documents'], ParticipantDocuments);

export const useParticipantDocumentsMinimalQuery = createGqlQuery<
  Types.ParticipantDocumentsMinimalQueryVariables,
  Types.ParticipantDocumentsMinimalQuery
>((variables) => ['participant', variables, 'documents', 'minimal'], ParticipantDocumentsMinimal);

export const useParticipantDocumentQuery = createGqlQuery<
  Types.ParticipantDocumentQueryVariables,
  Types.ParticipantDocumentQuery
>((variables) => ['participant', variables, 'document'], ParticipantDocument);

export const useParticipantDocumentVersionsQuery = createGqlQuery<
  Types.ParticipantDocumentVersionsQueryVariables,
  Types.ParticipantDocumentVersionsQuery
>((variables) => ['participant', variables, 'document', 'versions'], ParticipantDocumentVersions);

export const useParticipantDocumentVersionQuery = createGqlQuery<
  Types.ParticipantDocumentVersionQueryVariables,
  Types.ParticipantDocumentVersionQuery
>((variables) => ['participant', variables, 'document', 'version'], ParticipantDocumentVersion);

export const useParticipantWidgetLayoutItemsQuery = createGqlQuery<
  Types.ParticipantWidgetLayoutItemsQueryVariables,
  Types.ParticipantWidgetLayoutItemsQuery
>((variables) => ['participant', variables, 'widgets'], ParticipantWidgetLayoutItems);

export const useParticipantNotesFilterQuery = (
  participantId: string,
  filter: ParticipantNotesFilter,
) => {
  const { organization } = useAppContext();

  const query = createPaginatedGqlQuery<
    Types.ParticipantNotesQueryVariables,
    Types.ParticipantNotesQuery
  >(getParticipantNotesBaseKey, ParticipantNotes, {
    filter,
    getPaginationResponse: (response) => {
      return response?.organization?.participant?.notes?.pagination;
    },
  });

  const resp = query({
    organizationId: organization.id,
    participantId,
    request: {
      content: filter.contentFilter,
      labels: filter.labelFilter,
      pagination: {
        limit: filter.pageSize,
        offset: filter.currentPageNumber * filter.pageSize,
      },
    },
  });

  const notes =
    (resp.data?.organization?.participant?.notes.results as Common.ParticipantNote[]) ?? [];

  return { ...resp, participantNotes: notes };
};

export const useParticipantFilesAndDocumentsFilterQuery = (
  participantId: string,
  filter: ParticipantFilesAndDocumentsFilter,
) => {
  const { organization } = useAppContext();

  const [selectedRows, onRowsSelect] = React.useState<
    (Common.ParticipantFile | Common.ParticipantDocument)[]
  >([]);

  const filesQuery = useParticipantFilesQuery({
    organizationId: organization.id,
    participantId,
  });

  const documentsQuery = useParticipantDocumentsQuery({
    organizationId: organization.id,
    participantId,
  });

  const rowSelection: RowSelectionOptions<Common.ParticipantFile | Common.ParticipantDocument> = {
    selectedRows,
    onRowsSelect,
  };

  const isLoading = filesQuery.isLoading || documentsQuery.isLoading;
  const error = filesQuery.error || documentsQuery.error;

  const filesAndDocuments = React.useMemo(() => {
    const files =
      (filesQuery.data?.organization?.participant?.files as Common.ParticipantFile[]) ?? [];
    const documents =
      (documentsQuery.data?.organization?.participant?.documents as Common.ParticipantDocument[]) ??
      [];
    const filesAndDocuments = P.orderBy([...files, ...documents], (f) => f.createdAt, 'desc');

    const filteredByName = filesAndDocuments.filter((document) => {
      return document.name.toLowerCase().includes(filter.nameFilter.toLowerCase());
    });
    const filteredByLabels = !P.isEmpty(filter.labelFilter ?? [])
      ? filesAndDocuments.filter((document) => {
          return document.labels.some((label) => filter.labelFilter?.includes(label.id));
        })
      : filesAndDocuments;

    return P.intersect(filteredByName, filteredByLabels);
  }, [
    documentsQuery.data?.organization?.participant?.documents,
    filesQuery.data?.organization?.participant?.files,
    filter.labelFilter,
    filter.nameFilter,
  ]);

  return { isLoading, error, filesAndDocuments, rowSelection };
};

export const useParticipantDocumentVersionsInfiniteQuery = (
  variables: Types.ParticipantDocumentVersionsQueryVariables,
  options?: InfiniteQueryOptions<Types.ParticipantDocumentVersionsQuery>,
) => {
  const fetcher = useParticipantDocumentVersionsQuery.getFetcher();

  const query = useInfiniteQuery(
    useParticipantDocumentVersionsQuery.getKey(variables),
    async ({ pageParam }) => {
      return fetcher({
        ...variables,
        request: {
          ...variables.request,
          pagination: {
            ...variables.request?.pagination,
            offset: pageParam,
          },
        },
      });
    },
    {
      ...options,
      getNextPageParam: (lastPage: any) => {
        const pagination = lastPage?.organization?.participant?.document?.versions?.pagination;

        if (!pagination?.hasNextPage) {
          return undefined;
        }

        return pagination?.currentPage * pagination?.limit;
      },
    },
  );

  return query;
};

export const useParticipantNoteVersionsInfiniteQuery = (
  variables: Types.ParticipantNoteVersionsQueryVariables,
  options?: InfiniteQueryOptions<Types.ParticipantNoteVersionsQuery>,
) => {
  const fetcher = useParticipantNoteVersionsQuery.getFetcher();

  const query = useInfiniteQuery(
    useParticipantNoteVersionsQuery.getKey(variables),
    async ({ pageParam }) => {
      return fetcher({
        ...variables,
        request: {
          ...variables.request,
          pagination: {
            ...variables.request?.pagination,
            offset: pageParam,
          },
        },
      });
    },
    {
      ...options,
      getNextPageParam: (lastPage: any) => {
        const pagination = lastPage?.organization?.participant?.note?.versions?.pagination;

        if (!pagination?.hasNextPage) {
          return undefined;
        }

        return pagination?.currentPage * pagination?.limit;
      },
    },
  );

  return query;
};

export const useParticipantAuditTrailEntriesQuery = (
  variables: Types.ParticipantAuditTrailEntriesQueryVariables,
) => {
  const { organization } = useAppContext();

  const [params, setParams] = React.useState({
    lastSeen: variables.request?.pagination?.lastSeen ?? undefined,
    firstSeen: variables.request?.pagination?.firstSeen ?? undefined,
    pageSize: variables.request?.pagination?.limit ?? 10,
  });

  const setPageSize = (pageSize: number) => {
    setParams({ ...params, pageSize, lastSeen: undefined, firstSeen: undefined });
  };

  const setLastSeen = (lastSeen: DateTime) => {
    setParams({ ...params, lastSeen, firstSeen: undefined });
  };

  const setFirstSeen = (firstSeen: DateTime) => {
    setParams({ ...params, firstSeen, lastSeen: undefined });
  };

  const query = createTimestampPaginatedGqlQuery<
    Types.ParticipantAuditTrailEntriesQueryVariables,
    Types.ParticipantAuditTrailEntriesQuery
  >((variables) => ['participant', variables, 'events'], ParticipantAuditTrailEntries, {
    filter: {
      lastSeen: params.lastSeen,
      firstSeen: params.firstSeen,
      pageSize: params.pageSize,
      pageSizeOptions: [10, 20, 30],
      showTotal: (total: number) => `${total} entries`,
      setPageSize,
      setLastSeen,
      setFirstSeen,
    },
    getPaginationResponse: (response) => {
      return response?.organization?.participant?.entries.pagination;
    },
  });

  const resp = query({
    organizationId: organization.id,
    participantId: variables.participantId,
    request: {
      pagination: {
        lastSeen: params.lastSeen,
        firstSeen: params.firstSeen,
        limit: params.pageSize,
      },
    },
  });

  const entries =
    (resp.data?.organization?.participant?.entries.results as Common.AuditTrailEntry[]) ?? [];

  return {
    ...resp,
    entries,
  };
};
